import React from 'react';
import styled from 'styled-components';
import { FaGraduationCap, FaTractor, FaStore, FaUserTie, FaStethoscope, FaChalkboardTeacher } from 'react-icons/fa';
import { agentProfileScreen1, agentProfileScreen2, agentProfileScreen3, agentProfileScreen4 } from '../assets';

const ShowcaseSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: var(--white);
  
  span {
    color: var(--accent-light);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

const ShowcaseContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AgentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
`;

const AgentCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  color: var(--white);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const AgentIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--accent-color), var(--accent-dark));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  color: var(--white);
`;

const AgentTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const AgentDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
  line-height: 1.5;
`;

const MockupContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    transform: scale(0.9);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    transform: scale(0.8);
  }
`;

const PhoneMockup = styled.div`
  position: relative;
  border-radius: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 10px solid #333;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
  
  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 40%;
    height: 25px;
    background: #333;
    border-radius: 0 0 20px 20px;
    top: 0;
    left: 30%;
    z-index: 2;
  }
`;

const AppImage = styled.img`
  width: 100%;
  height: 300px;
  display: block;
`;

const AgentShowcase = () => {
  const agents = [
    {
      icon: <FaGraduationCap />,
      title: "Student Assistant",
      description: "Helps with studies, exam preparation, and academic guidance across subjects."
    },
    {
      icon: <FaTractor />,
      title: "Farmer's Guide",
      description: "Provides crop advice, weather updates, and agricultural best practices."
    },
    {
      icon: <FaStore />,
      title: "Business Advisor",
      description: "Assists rural businesses with growth strategies and market insights."
    },
    {
      icon: <FaUserTie />,
      title: "Career Coach",
      description: "Guides job seekers with career planning and skill development."
    },
    {
      icon: <FaStethoscope />,
      title: "Health Guide",
      description: "Offers basic health information and wellness tips in local languages."
    },
    {
      icon: <FaChalkboardTeacher />,
      title: "Skill Trainer",
      description: "Helps develop new skills with personalized learning paths."
    }
  ];

  const mockupScreens = [
    {
      image: agentProfileScreen1,
      alt: "Student Assistant Interface"
    },
    {
      image: agentProfileScreen2,
      alt: "Farmer's Guide Interface"
    },
    {
      image: agentProfileScreen3,
      alt: "Business Advisor Interface"
    },
    {
      image: agentProfileScreen4,
      alt: "Health Guide Interface"
    }
  ];

  return (
    <ShowcaseSection id="agents">
      <Container>
        <SectionHeader>
          <SectionTitle>Specialized <span>AI Agents</span></SectionTitle>
          <SectionSubtitle>
            Our AI agents are designed to assist different user groups with domain expertise
            and support in local languages.
          </SectionSubtitle>
        </SectionHeader>

        <ShowcaseContent>
          <AgentsList>
            {agents.map((agent, index) => (
              <AgentCard key={index}>
                <AgentIcon>{agent.icon}</AgentIcon>
                <AgentTitle>{agent.title}</AgentTitle>
                <AgentDescription>{agent.description}</AgentDescription>
              </AgentCard>
            ))}
          </AgentsList>

          <MockupContainer>
            {mockupScreens.map((screen, index) => (
              <PhoneMockup key={index}>
                <AppImage src={screen.image} alt={screen.alt} />
              </PhoneMockup>
            ))}
          </MockupContainer>
        </ShowcaseContent>
      </Container>
    </ShowcaseSection>
  );
};

export default AgentShowcase; 